import PropTypes from 'prop-types';
import React from 'react';

/**
 * The tab strip used by the embedded directory widgets.
 *
 * Replaces manageTabs(), which is duplicated byte-for-byte between
 * directory/insurer-widget.js and directory/widget.js. That version wrote
 * colours straight onto style attributes and dispatched a synthetic event
 * built with the deprecated createEvent/initEvent pair; here the parent owns
 * which tab is active and the accent is a CSS custom property.
 *
 * THE MARKUP IS NOT FREE TO CHANGE. All 17 per-fund stylesheets in
 * directory/static/directory/style/widget-*.css target
 * `.hs-nav .hs-nav-tabs li a`, and the colour block Django renders into the
 * page targets `.hs-nav-tabs .active.custom-section-color`. Anchors, those
 * class names, and `active` on both the <li> and the <a> are all load-bearing:
 * swap the anchor for a button and every fund's tab styling silently stops
 * applying.
 */
export default function Tabs({
    accentColour = null,
    activeTab,
    onChange,
    tabs,
}) {
    const tabRefs = React.useRef([]);

    function select(event, id) {
        // The href is a real fragment so the tabs still mean something with
        // JS off, but we own the activation.
        event.preventDefault();
        onChange(id);
    }

    function handleKeyDown(event, index) {
        const last = tabs.length - 1;
        let next = null;

        if (event.key === 'ArrowRight') {
            next = index === last ? 0 : index + 1;
        } else if (event.key === 'ArrowLeft') {
            next = index === 0 ? last : index - 1;
        } else if (event.key === 'Home') {
            next = 0;
        } else if (event.key === 'End') {
            next = last;
        }

        if (next !== null) {
            event.preventDefault();
            onChange(tabs[next].id);
            // Selection alone is not enough: only the active tab is tabbable,
            // so leaving focus behind puts it on an element that no longer
            // takes the tab key, and a screen reader keeps announcing the tab
            // the member has just moved away from.
            const target = tabRefs.current[next];
            if (target) {
                target.focus();
            }
        }
    }

    return (
        <div className="hs-nav" id="tabs">
            <ul className="hs-nav-tabs" role="tablist">
                {tabs.map((tab, index) => {
                    const isActive = tab.id === activeTab;
                    return (
                        <li className={isActive ? 'active' : null} key={tab.id}>
                            <a
                                aria-controls={`${tab.id}-panel`}
                                aria-selected={isActive}
                                className={
                                    isActive
                                        ? 'active custom-section-color'
                                        : null
                                }
                                href={`#${tab.id}`}
                                id={`${tab.id}-tab`}
                                onClick={(event) => select(event, tab.id)}
                                onKeyDown={(event) =>
                                    handleKeyDown(event, index)
                                }
                                ref={(el) => {
                                    tabRefs.current[index] = el;
                                }}
                                role="tab"
                                style={
                                    accentColour
                                        ? {'--tab-accent': `#${accentColour}`}
                                        : undefined
                                }
                                tabIndex={isActive ? 0 : -1}
                            >
                                {tab.label}
                            </a>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}

Tabs.propTypes = {
    // Hex without the leading #, matching the widget's query parameters.
    accentColour: PropTypes.string,
    activeTab: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    tabs: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            label: PropTypes.node.isRequired,
        }),
    ).isRequired,
};

